/* eslint-disable no-unused-vars -- Article creation from a transfer order is built but not wired to any control yet. */
import {closeDialog, DataStatus, openDialog, TabProps, TabsHeader} from 'platform/components';
import {Box, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNil} from 'ramda';
import {isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {useGetTransferOrderQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {WAREHOUSE_GRID_CODES, refreshDmsDatagrid, usePermissions} from '@dms/shared';
import {
  ArticleCreate,
  ArticleCreateDefaultValues,
  getSingleOrUndefined,
} from '@dms/shared-aftersales';

import {composePath, useNavigate} from 'shared/web';

import type {Filters} from 'features/eag-grid';

import {TransferOrderMaterialWarehouseTab} from './TransferOrderMaterialWarehouseTab';

const CREATE_ARTICLE_DIALOG = 'createArticleDialog';

interface TransferOrderMaterialListProps {
  transferOrderId: string;
  onAddToBasket: (articleId: string) => Promise<void>;
  hasPendingRequest: boolean;
}

export function TransferOrderMaterialList(props: TransferOrderMaterialListProps) {
  const [activeTabId, setActiveTabId] = useState(TABS_IDS.warehouseTab);
  const [activeFolderId, setActiveFolderId] = useState<string>();
  const filters = useRef<ArticleCreateDefaultValues>({});
  const navigate = useNavigate();

  const [canCreateArticle] = usePermissions({
    permissionKeys: ['articleCreate'],
  });

  const transferOrderQuery = useGetTransferOrderQuery({transferOrderId: props.transferOrderId});

  const handleArticleDetail = (warehouseId: string, articleId: string) => {
    const path = composePath(warehouseRoutes.articleDetailOverview, {
      params: {warehouseId, id: articleId},
      isSearchParamsPreserved: false,
    });
    window.open(path, '_blank');
  };

  const handleFiltersChange = (newFilters: Filters) => {
    const articleCreateDefaultValues: ArticleCreateDefaultValues = {};

    if (isString(newFilters.catalogNumber)) {
      articleCreateDefaultValues.catalogNumber = newFilters.catalogNumber;
    }

    if (isNotNilOrEmpty(newFilters.manufacturerId)) {
      articleCreateDefaultValues.manufacturerId = getSingleOrUndefined(newFilters.manufacturerId);
    }

    if (isString(newFilters.name)) {
      articleCreateDefaultValues.name = newFilters.name;
    }

    filters.current = articleCreateDefaultValues;
  };

  const handleCreateArticle = () => {
    openDialog(
      <ArticleCreate
        defaultValues={{
          activeFolderId,
          catalogNumber: filters.current?.catalogNumber,
          manufacturerId: filters.current?.manufacturerId,
          warehouseId: transferOrderQuery.data?.sourceWarehouseId,
          name: filters.current?.name,
        }}
        onArticleCreate={(createdArticleData) => {
          if (createdArticleData.submittedButton === 'CREATE_AND_EDIT') {
            return navigate(
              composePath(warehouseRoutes.articleDetailOverview, {
                params: {
                  warehouseId: createdArticleData.formValues.warehouseId,
                  id: createdArticleData.articleId,
                },
                isSearchParamsPreserved: false,
              })
            );
          }
          closeDialog(CREATE_ARTICLE_DIALOG);
          refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseTransferOrderBasket);
        }}
        submitButtonTitle={i18n.t('general.actions.create')}
        onClose={() => closeDialog(CREATE_ARTICLE_DIALOG)}
        hasCreateAndEditButton
        data-testid="createArticleDialog"
      />,
      {
        id: CREATE_ARTICLE_DIALOG,
        title: i18n.t('entity.warehouse.labels.createNewArticle'),
        withAdditionalFooter: true,
        scrollBehavior: 'outside',
      }
    );
  };

  if (isNil(transferOrderQuery.data?.sourceWarehouseId)) {
    return (
      <DataStatus
        isError
        errorMessage={i18n.t('entity.warehouse.labels.sourceWarehouseIsNotDefined')}
        minHeight="100%"
      />
    );
  }

  const tabs: TabProps[] = [
    {
      id: TABS_IDS.warehouseTab,
      title: i18n.t('entity.warehouse.labels.warehouse'),
      content: (
        <TransferOrderMaterialWarehouseTab
          transferOrderId={props.transferOrderId}
          warehouseId={transferOrderQuery.data.sourceWarehouseId}
          onAddToBasket={props.onAddToBasket}
          onArticleDetail={handleArticleDetail}
          hasPendingRequest={props.hasPendingRequest}
        />
      ),
    },
  ];

  const activeTabContent = tabs.find(
    (tab) => tab.id === (activeTabId ?? TABS_IDS.warehouseTab)
  )?.content;

  return (
    <VStack spacing={4} height="100%">
      <TabsHeader
        variant="condensed"
        tabs={tabs}
        activeTabId={activeTabId}
        onChange={setActiveTabId}
      />
      <Box height="100%" flex={1}>
        {activeTabContent}
      </Box>
    </VStack>
  );
}

const TABS_IDS = {
  warehouseTab: 'transferOrder-addMaterial-warehouseTab',
};
